import type { CSSProperties } from 'react';
import { PrimaryGiftIcon } from '../icons/TierIcons';
import { getTierBorderGradient } from '../utils/tierGradients';
import {
  TIER_0_UNLOCK_ACCESS_CTA,
  TIER_0_WELCOME_HEADLINE,
  TIER_0_WELCOME_STATUS_LINE,
} from './tier0WelcomeHubCopy';
import { Tier0UnlockAccessLockIcon } from './Tier0UnlockAccessLockIcon';

const PLAYCAST_REWARDS_GOLD = '#FBBF24';
const TIER_0_ICON_GLYPH_RED = '#FCA5A5';
const TIER_0_ICON_RING_RED = '#F87171';
const TIER_0_ICON_FILL_RED = '#450A0A';

interface Tier0WelcomeUnlockCardProps {
  onLearnMore: () => void;
  /** Defaults to settings/hub “How to unlock access” copy. */
  unlockCtaLabel?: string;
  /** Ambient wash behind the card face. Defaults to the shared purple wash. */
  ambientGradient?: string;
}

const DEFAULT_AMBIENT_GRADIENT = 'linear-gradient(210deg, rgba(139,0,255,0.2), transparent)';

/**
 * Red-shifted ambient wash for the Tier 0 unlock card face.
 * Lightest in the horizontal middle (behind the title/description text for legibility),
 * rising gently toward each edge. Red only.
 */
export const TIER_0_UNLOCK_RED_AMBIENT_GRADIENT =
  'linear-gradient(90deg, rgba(239,68,68,0.17) 0%, rgba(239,68,68,0.03) 50%, rgba(239,68,68,0.08) 100%)';

/** Tier 0 welcome: status + opportunity copy and unlock CTA. */
export const Tier0WelcomeUnlockCard = ({
  onLearnMore,
  unlockCtaLabel = TIER_0_UNLOCK_ACCESS_CTA,
  ambientGradient = DEFAULT_AMBIENT_GRADIENT,
}: Tier0WelcomeUnlockCardProps) => {
  const headline = TIER_0_WELCOME_HEADLINE;
  const statusLine = TIER_0_WELCOME_STATUS_LINE;
  const borderGradient = getTierBorderGradient('tier-0');

  return (
    <div
      style={{
        width: '100%',
        padding: 2,
        borderRadius: 10,
        background: borderGradient,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          borderRadius: 8,
          background: '#0b0618',
          position: 'relative',
          overflow: 'hidden',
          padding: 10,
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: ambientGradient,
            pointerEvents: 'none',
          }}
        />
        {/* Red glow emanating from behind the status icon (top-left, off the text) */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 29px 29px, rgba(239,68,68,0.09) 0%, rgba(239,68,68,0.03) 30%, transparent 55%)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <p
            style={{
              margin: '0 0 6px',
              color: '#ffffff',
              fontSize: 17,
              lineHeight: 1.15,
              fontWeight: 700,
            }}
          >
            {headline}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PrimaryGiftIcon
                size={22}
                circleFill={TIER_0_ICON_FILL_RED}
                circleStroke={TIER_0_ICON_RING_RED}
                innerFill={TIER_0_ICON_GLYPH_RED}
              />
            </div>
            <p
              style={{
                margin: 0,
                color: '#8d8aa8',
                fontSize: 11,
                lineHeight: '15px',
                fontWeight: 500,
                flex: 1,
                minWidth: 0,
              }}
            >
              {statusLine}
            </p>
          </div>

          <button
            type="button"
            onClick={onLearnMore}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              width: '100%',
              minHeight: 30,
              marginTop: 8,
              padding: '0 12px',
              borderRadius: 9999,
              border: 'none',
              background: PLAYCAST_REWARDS_GOLD,
              color: '#000000',
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            <Tier0UnlockAccessLockIcon color="#000000" />
            {unlockCtaLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export const TIER_0_WELCOME_CATALOG_BUTTON_STYLE: CSSProperties = {
  width: '100%',
  minHeight: 44,
  padding: '12px 24px',
  borderRadius: 9999,
  border: 'none',
  background: '#FBBF24',
  color: '#000000',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 15,
  boxSizing: 'border-box',
};
